import { CSRF_TOKEN_NAME } from '@/constants'
import CustomError from '../CustomError'
import { getCookie, getCsrf } from './authentication'
import { client } from './client'

/**
 * List tools
 * @returns All tools in the user's organization
 */
export const listTools = async ({
  limit,
  offset,
  search,
  documentVersionId,
}: {
  limit?: number
  offset?: number
  search?: string
  documentVersionId?: string
}) => {
  const resp = await client.GET('/api/v1/projects/tools', {
    params: {
      query: {
        limit,
        offset,
        search,
        /**
         * Optional server-side scope:
         * when provided we only list tools related to a document version.
         */
        document_version_id: documentVersionId,
      },
    },
  })

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to list tools'
    throw new CustomError(resp, message)
  }

  return {
    tools: resp.data.items,
    totalTools: resp.data.count,
    limit,
    offset,
    search,
    documentVersionId,
  }
}

/**
 * Create a new tool
 * @param values
 * @returns newly created tool
 */
export const createTool = async (values: { name: string }) => {
  await getCsrf()

  const headers = {
    'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
  }

  const resp = await client.POST('/api/v1/projects/tools', {
    headers,
    body: values,
  })

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to create tool'
    throw new CustomError(resp, message)
  }

  return resp.data
}

/**
 * Update a tool
 * @param toolId
 * @param values
 * @returns updated tool
 */
export const updateTool = async (toolId: string, values: { name: string }) => {
  await getCsrf()

  const headers = {
    'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
  }

  const resp = await client.PUT('/api/v1/projects/tools/{tool_id}', {
    headers,
    params: {
      path: { tool_id: toolId },
    },
    body: values,
  })

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to update tool'
    throw new CustomError(resp, message)
  }

  return resp.data
}

/**
 * Delete a tool
 * @param toolId
 * @returns
 */
export const deleteTool = async (toolId: string) => {
  await getCsrf()

  const headers = {
    'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
  }

  const resp = await client.DELETE('/api/v1/projects/tools/{tool_id}', {
    headers,
    params: { path: { tool_id: toolId } },
    // Tools delete returns an empty 200 response body.
    // Parse as text to avoid JSON parse errors on empty payloads.
    parseAs: 'text',
  })

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to delete tool'
    throw new CustomError(resp, message)
  }

  return true
}
